<template>
  <div>
    <app-header
      @cancel-transaction="cancelCurrentTransaction"
      @show-cancel-modal="showCancelModal"
      @hide-cancel-modal="hideCancelModal"
      :is-show-retry="canRetryTransaction"
      :close-cancel-transaction-modal="closeCancelTransactionModal"
    ></app-header>
    <loading
      v-if="message.request.isProcessing || loadingManagerState.isLoading"
      :instruction="message.instruction.message || loadingManagerState.message"
    />
    <router-view
      v-else
      :request="message.request"
      :instruction="message.instruction"
      :sidMessage="message.sidMessage"
      @send-response="sendResponse"
    />
  </div>
</template>

<script>
import { mapGetters, mapState, mapActions } from 'vuex'
import Header from '@/components/Header'
import Loading from '@/components/Loading'
import Constants from '@/constants'
import {
  isCapitecPayAcquirer,
  isNedbankPayAcquirer,
  isAbsaPayAcquirer,
  isOpenBankingAcquirer,
  isNotUndefinedOrNull,
} from '@/utils'

import {
  receiveRequest,
  receiveInstruction,
  checkSignalRConnection,
  joinGroup,
  connectSignalR,
  sendAcknowledgement,
  sendInstruction,
  sendResponse,
  receiveSidMessage,
} from '@/utils/signalRHelpers'
import { blockBackNavigationMixin } from '@/mixins/blockBackNavigationMixin'
import axios from '@/wrapper'
import LoadingManager from '@/LoadingManager'
import { isTransactionCompletedStatus } from '../utils'
import { cancelTransaction } from '@/services/transactionService'

export default {
  name: 'SignalR',
  components: {
    'app-header': Header,
    Loading,
  },
  mixins: [blockBackNavigationMixin],

  data() {
    return {
      closeCancelTransactionModal: +new Date(),
      timeout: 0,
      loadingManagerState: LoadingManager.getState(), // Directly access the state,
    }
  },

  computed: {
    ...mapGetters('merchantSettings', ['canRetryTransaction']),
    ...mapState('signalRMessage', ['message']),
    ...mapState('transaction', ['transaction']),

    transactionId() {
      return this.$route.query.transactionId
    },

    handleNedbankAuthRedirect() {
      return !!this.$route.query.isHandleNedbankAuth
    },
  },

  async created() {
    if (!isTransactionCompletedStatus(this.transaction.status)) {
      if (isCapitecPayAcquirer(this.transaction.acquirerCode)) {
        await this.handleCapitecPersistence()
        //this.isShowInstruction = false
        LoadingManager.setLoading(false)
      }

      if (isNedbankPayAcquirer(this.transaction.acquirerCode)) {
        LoadingManager.setLoading(false)
        await this.handleNedbankPersistence()
      }

      if (isAbsaPayAcquirer(this.transaction.acquirerCode)) {
        await this.handleAbsaPersistence()
      }
    }
    this.subscribeEvents()
    await this.connectSignalR(this.transactionId)
    this.checkSignalRConnection()
    await this.joinGroup(this.transactionId)
  },

  // This method runs when reloading browser, navigating usually and when user uses back button
  async updated() {
    // Detect that back button was used to load this page
    window.onpopstate = function () {
      // Reload page, re-initializing Paysid correctly waiting for a instruction
      window.location.reload()
    }
  },

  beforeDestroy() {
    window.clearInterval(this.checkSignalRConnectionIntervalId)
    window.clearInterval(this.cancelTimeoutIntervalId)
    this.unsubscribeEvents()
  },

  methods: {
    ...mapActions('appSettings', ['setError']),
    ...mapActions('transaction', ['setTimeToLive', 'cancelTransaction']),
    ...mapActions('signalRMessage', [
      'setRequest',
      'setInstruction',
      'setSidMessage',
    ]),
    connectSignalR,
    receiveRequest,
    receiveInstruction,
    checkSignalRConnection,
    joinGroup,
    receiveSidMessage,
    sendAcknowledgement,
    sendInstruction,
    sendResponse,
    subscribeEvents() {
      this.$signalRHubProxy.on('sendRequest', this.receiveRequest)
      this.$signalRHubProxy.on('sendInstructionAsync', this.receiveInstruction)
      this.$signalRHubProxy.on('sendInstruction', this.receiveInstruction)
      this.$signalRHubProxy.on('sendSidMessageAsync', this.receiveSidMessage)
    },
    unsubscribeEvents() {
      this.$signalRHubProxy.off('sendRequest', this.receiveRequest)
      this.$signalRHubProxy.off('sendInstructionAsync', this.receiveInstruction)
      this.$signalRHubProxy.off('sendInstruction', this.receiveInstruction)
      this.$signalRHubProxy.off('sendSidMessageAsync', this.receiveSidMessage)
    },
    addCancelTimeout() {
      this.cancelTimeoutIntervalId = window.setInterval(() => {
        this.timeout = this.timeout + 1000
        if (this.timeout > Constants.cancelTransactionTimeout) {
          const error = new Error('User took too long to decide to cancel')
          this.$handleException({ error, transactionId: this.transactionId })
        }
      }, 1000)
    },
    showCancelModal() {
      this.addCancelTimeout()
      this.unsubscribeEvents()
    },
    hideCancelModal() {
      window.clearInterval(this.cancelTimeoutIntervalId)
      this.subscribeEvents()
    },
    async cancelBankApiTransaction() {
      const transactionId = this.transactionId

      this.$logInfo({
        transactionId,
        message: Constants.logMessages.cancelOnBankSelect,
      })
      this.closeCancelTransactionModal = +new Date()

      try {
        await cancelTransaction({
          transactionId,
        })
      } catch (error) {
        this.$logException({ error, transactionId: this.transactionId })
        return
      }

      await this.$router.replace({
        name: 'cancelled',
        query: { transactionId },
      })
    },
    async cancelPuppeteerTransaction() {
      this.message.instruction.messageId = +new Date()
      this.message.instruction.instructionType =
        Constants.instructionTypes.MESSAGE
      this.message.instruction.message = 'Attempting to cancel transaction'
      try {
        this.$logInfo({
          transactionId: this.transactionId,
          message: Constants.logMessages.cancelTransaction,
        })

        await this.sendInstruction(
          this.transactionId,
          'User cancelled',
          1,
          null
        )
        this.closeCancelTransactionModal = +new Date()
      } catch (error) {
        this.$handleException({ error, transactionId: this.transactionId })
      }
    },
    async cancelCurrentTransaction() {
      if (isOpenBankingAcquirer(this.transaction.acquirerCode)) {
        await this.cancelBankApiTransaction()
      } else {
        await this.cancelPuppeteerTransaction()
      }
    },

    async handleCapitecPersistence() {
      let timeToLive
      let expiryDate
      const handleException = this.$handleException
      const transactionId = this.transactionId

      await axios
        .post(
          `v30/BankApi/GetStatus`,
          {
            acquirerCode: this.transaction.acquirerCode,
            transactionId: this.transaction.id,
          },
          {}
        )
        .then(function (response) {
          if (
            isNotUndefinedOrNull(response.data) &&
            isNotUndefinedOrNull(response.data.AcquirerResponseDto)
          ) {
            const data = JSON.parse(response.data.AcquirerResponseDto)
            timeToLive = data.TimeToLive ?? null
            expiryDate = data.ExpiryDate ?? null
          } else {
            const error = new Error(
              `Response Object Error: ${JSON.stringify(response.data)}`
            )
            handleException({ error, transactionId })
          }
        })
        .catch(function (error) {
          handleException({ error, transactionId })
        })
      this.setTimeToLive(timeToLive)
      const redirectPage = expiryDate ? 'countdownTimer' : 'login'

      await this.$router
        .push({
          name: redirectPage,
          query: { transactionId: this.transactionId },
        })
        .catch((e) => {
          console.info(e)
        })
    },
    async handleNedbankPersistence() {
      const params = this.$route.query
      const routeName = isNotUndefinedOrNull(params.code)
        ? 'nedbankFinalisePayment'
        : 'login'

      this.setRequest({ ...this.message.request, isProcessing: false })
      await this.$router
        .push({
          name: routeName,
          query: { ...params, transactionId: this.transactionId },
        })
        .catch((e) => {
          console.info(e)
        })
    },
    async handleAbsaPersistence() {
      LoadingManager.setLoading(false)

      let routeName = 'login'
      const { sidMessage } = this.message
      if (isNotUndefinedOrNull(sidMessage)) {
        if (sidMessage.requestType) routeName = sidMessage.getRequestPage()

        if (sidMessage.instructionType && !sidMessage.isBehaviourNone())
          routeName = sidMessage.getInstructionPage()

        if (sidMessage.isMessage()) {
          LoadingManager.setMessage(sidMessage.message)
        }
        LoadingManager.setLoading(sidMessage.isProcessing)

        if (!sidMessage.options.Success) {
          if (sidMessage.isCancelled()) {
            routeName = 'cancelled'
          }
        }
      }

      await this.$router
        .push({
          name: routeName,
          query: { transactionId: this.transactionId },
        })
        .catch((e) => {
          this.$handleException({
            error: e,
            isLogError: false,
            isHandleError: false,
            transactionId: this.transactionId,
          })
        })
    },
  },
}
</script>
